const SEARCH_MIN_LENGTH = 2;

export const normalizeSearchQuery = (query) => String(query || '').trim();

export const escapeRegex = (query) =>
  String(query || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const createSafeSearchRegex = (query, flags = 'i') => {
  const normalizedQuery = normalizeSearchQuery(query);
  if (!normalizedQuery) return null;
  return new RegExp(escapeRegex(normalizedQuery), flags);
};

export const isLoadedSurfaceSearchActive = (
  query,
  minLength = SEARCH_MIN_LENGTH,
) => normalizeSearchQuery(query).length >= minLength;

const stripHtmlTags = (value) => String(value || '').replace(/<[^>]*>/g, ' ');

const normalizeSearchText = (values = []) =>
  values
    .map(stripHtmlTags)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

export const getSearchableTextForPost = (post = {}) =>
  normalizeSearchText([
    post.title,
    post.desc,
  ]);

export const getSearchableTextForMeme = (meme = {}) =>
  normalizeSearchText([meme.title]);

export const filterLoadedItems = (
  items = [],
  query,
  getSearchableText,
  minLength = SEARCH_MIN_LENGTH,
) => {
  if (!Array.isArray(items)) return [];
  if (!isLoadedSurfaceSearchActive(query, minLength)) return items;

  const regex = createSafeSearchRegex(query);
  if (!regex) return items;

  return items.filter((item) => regex.test(getSearchableText(item)));
};

export const getHighlightedParts = (text, query) => {
  const value = String(text || '');
  const regex = createSafeSearchRegex(query, 'gi');

  if (!regex || !value) {
    return value ? [{ text: value, match: false }] : [];
  }

  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(value)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        text: value.slice(lastIndex, match.index),
        match: false,
      });
    }

    parts.push({
      text: match[0],
      match: true,
    });

    lastIndex = match.index + match[0].length;

    if (regex.lastIndex === match.index) {
      regex.lastIndex += 1;
    }
  }

  if (lastIndex < value.length) {
    parts.push({
      text: value.slice(lastIndex),
      match: false,
    });
  }

  return parts;
};

export const getMatchedSnippet = (text, query, contextLength = 72) => {
  const value = normalizeSearchText([text]);
  if (!value) return '';

  const regex = createSafeSearchRegex(query);
  const match = regex?.exec(value);
  if (!match) {
    return value.length > contextLength * 2
      ? `${value.slice(0, contextLength * 2).trim()}...`
      : value;
  }

  const start = Math.max(0, match.index - contextLength);
  const end = Math.min(
    value.length,
    match.index + match[0].length + contextLength,
  );
  const prefix = start > 0 ? '...' : '';
  const suffix = end < value.length ? '...' : '';

  return `${prefix}${value.slice(start, end).trim()}${suffix}`;
};
